import GLRaycaster, { GLRaycasterParams } from "./glraycaster";
import PostProcessing from "./post-processing";
import WebVrWrapper from "./webvr";
import Network from "../miniviews/network";
import { fire, listen } from "../utils/events";
import AssessmentManager from "../assessment/assessment-manager";
import { updateMMMObjects } from "../meanMedianMode/meanMedianMode";
import { updateTerrainCars } from "../terrainCar/terrainCar";
import Utils from "../utils";
import { updateSplines } from "../assets/spline";
import { updateInteractableGeometries } from "../geometry-teaching/interactable-geometry";
import Input from "../interactivity/inputs";
import SlideManager from "../utils/slide-manager";
import getStyle from "../utils/get-style";
import Q3Background from "../assets/background";
import Q3Fog from "./fog";
import AssetDeserializer from "../assets/asset-deserializer";
import ensureArray from '../utils/ensure-array';
import Bounds from "../utils/bounds";
import getRenderData from "../helpers/get-renderdata";
import getLetterbox from "../helpers/get-letterbox";
import { addCameraProtection } from "../utils/add-protection";

import GroundPlane from "./ground-plane";
import SystemDefaults from "../system-defaults";

import FaceSorter from '../geometry/faceSorting';
import ObjectManager from "../objects/object-manager";
import getRelativeCoordinates from "../utils/get-relative-coordinates";

import { checkSeamless } from "./cubemap-seams/detect";
import ArraySet from "../utils/arraySet";

// Just so that someone doesn't get a bright idea to modify these directly.
/**
 * @type {THREE.WebGLRenderer}
 */
let rendererMain = null;

/**
 * @type {GLRaycaster}
 */
let webGLRaycaster = null;

/**
 * @type {PostProcessing}
 */
let postProcessing = null;

/**
 * @type {WebVrWrapper}
 */
let webVrWrapper = null;

let isInitialized = false;

const boundingRect = new THREE.Box2(), sceneBoundingBox = new THREE.Box3();
const viewPort = new Bounds(), scissorSize = new Bounds();
const PLANE_NEAR = 0.001, PLANE_FAR = 10000.0;

let sceneBoundingBoxResolved = false;
let sceneBoundingBoxResolve;
const sceneBoundingBoxPromise = new Promise((resolve, reject) => {
    sceneBoundingBoxResolve = resolve;
});

const tmpBoundingBox = new THREE.Box3(), tmpMatrix = new THREE.Matrix4(), tmpVec3 = new THREE.Vector3();

let vpWidth, vpHeight;
let supportedTextures;
let containsAnimatedObjects = false;
let passByRaycasterLoader;

const NAMESPACE_SVG = "http://www.w3.org/2000/svg"; //DevSkim: ignore DS137138
const overlay2d = document.createElement("div");
const domElement = document.createElement("div");
const canvasHolder = document.createElement("div");
const overlaySVG = document.createElementNS(NAMESPACE_SVG, "svg");
domElement.appendChild(canvasHolder);

canvasHolder.setAttribute("canvas-holder", "");

domElement.setAttribute("main-canvas", "");
domElement.id = "main-canvas";
domElement.style.zIndex = 4;

let fog = null;

/**
 * @type {Q3Background}
 */
let background = null;

function onFocus() {
    if (Q3.selected && Q3.selected.type === "LiveLabel" && Q3.selected.element.isActive && Q3.editMode) {
        Q3.selected.element.input.focus();
    }
}

function onLoseFocus() { }

domElement.addEventListener("mouseenter", onFocus, false);
domElement.addEventListener("mouseleave", onLoseFocus, false);

/**
 * @type {THREE.Scene}
 */
const scene = new THREE.Scene();
scene.name = "RootScene";

/**
 * @type {THREE.Scene}
 */
let activeScene = new THREE.Scene();

const camera = new THREE.PerspectiveCamera();
const cameraHolder = new THREE.Object3D(), cameraAnimator = new THREE.Object3D();
cameraHolder.name = "CameraHolder";
cameraAnimator.name = "CameraAnimator";
cameraAnimator.add(camera);
cameraHolder.add(cameraAnimator);
scene.add(cameraHolder);

addCameraProtection(camera);

const groundPlane = new GroundPlane();
scene.add(groundPlane);

if (Utils.queryParams.has("debug_scene_size")) {
    const boxHelper = new THREE.Box3Helper(sceneBoundingBox);
    scene.add(boxHelper);
}

let enableOutline = true, enableHighlight = true;

/**
 * @type {THREE.WebGLRendererParameters}
 */
let initGlParams;

/**
 * @type {GLRaycasterParams}
 */
let initRcParams;

let lastFaceCount = 0;
let isPaused = false;
const stats = new Stats();

const clock = new THREE.Clock();
const FPS_CAP = 30;
const interval = 1000 / FPS_CAP;
let wasDown = false;
let rendererRevision = -1, currentTime = 0;
let raycasterRevision = -1;

const g_size = new THREE.Vector2();

// (function () {
//     /**
//      *     5--------6
//      *    /|       /|
//      *   / |      / |
//      *  4--------7  |
//      *  |  |     |  |
//      *  |  0 --- |- 1
//      *  | /      | /
//      *  |/       |/
//      *  3--------2
//      */


//     const geometry = new THREE.BufferGeometry();
//     const attrPosition = new THREE.Float32BufferAttribute([
//         -0.5, -0.5, -0.5, // 0
//         +0.5, -0.5, -0.5, // 1
//         +0.5, -0.5, +0.5, // 2
//         -0.5, -0.5, +0.5, // 3
//         -0.5, +0.5, +0.5, // 4
//         -0.5, +0.5, -0.5, // 5
//         +0.5, +0.5, -0.5, // 6
//         +0.5, +0.5, +0.5  // 7
//     ], 3);

//     const attrIndex = new THREE.Uint8BufferAttribute([
//         0, 1, 3,
//         3, 1, 2,
//         1, 0, 6,
//         6, 0, 5,
//         1, 7, 2,
//         7, 1, 6,
//         5, 7, 6,
//         7, 5, 4,
//         0, 4, 5,
//         4, 0, 3,
//         4, 3, 2,
//         2, 7, 4
//     ], 1);

//     geometry.addAttribute("position", attrPosition);
//     geometry.setIndex(attrIndex);

//     const mesh = new THREE.Mesh(
//         geometry,
//         new THREE.MeshBasicMaterial({ color: 0xff00ff, transparent: true, depthTest: false, depthWrite: false })
//     );

//     scene.add(mesh);
// })();

const RenderManager = new class RenderManager {
    constructor() {
        this.initializePromise = new Promise((resolve, reject) => {
            this._initializeResolve = resolve;
        });
    }

    get stats() { return stats; }
    get activeCamera() { return camera; }
    get activeScene() { return activeScene; }
    get background() { return background; }
    get fog() { return fog; }
    get groundPlane() { return groundPlane; }
    get containsAnimatedObjects() { return containsAnimatedObjects; }
    set containsAnimatedObjects(v) { containsAnimatedObjects = v; }

    clearCameraAsset() {
        Q3.mixer.stopObjectCurrentAnimation(cameraAnimator.uuid, -1);

        cameraHolder.scale.set(1, 1, 1);

        camera.getWorldPosition(camera.position);
        camera.getWorldQuaternion(camera.quaternion);

        cameraAnimator.name = "CameraAnimator";
        cameraAnimator.position.set(0, 0, 0);
        cameraAnimator.quaternion.set(0, 0, 0, 1);
        cameraAnimator.scale.set(1, 1, 1);

        camera.far = PLANE_FAR;
        camera.updateProjectionMatrix();

        cameraHolder.updateMatrixWorld(true);
    }

    setAssetCamera(animationUuid) {
        const regexp = /(^__Camera\.)([\w\d]+)\./;
        const animation = Q3.data.assets.animations[animationUuid];
        const controls = SlideManager.currentSlide.controls;
        const [, , camName] = animation.name.match(regexp);

        cameraAnimator.name = camName;

        cameraHolder.scale.fromArray(animation.scale);

        camera.position.fromArray(animation.position);
        camera.quaternion.fromArray(animation.rotation);

        camera.far = PLANE_FAR * (1 / cameraHolder.scale.x);
        camera.updateProjectionMatrix();

        Q3.mixer.loadAndPlay({
            objectUuid: cameraAnimator.uuid,
            animationUuid,
            loopType: controls.loopType,
            timescale: controls.animationSpeed
        });
    }

    tryRestoreContext() {
        if (!isInitialized) throw new Error("Can't restore uninitialized content.");

        rendererMain.domElement.remove();
        rendererMain.dispose();

        rendererMain = new THREE.WebGLRenderer(initGlParams);
        canvasHolder.appendChild(rendererMain.domElement);

        rendererMain.context.canvas.addEventListener("webglcontextlost", function (event) {
            console.error(event);
        }, false);

        function disposeObjectContext(scene) {
            scene.traverse(object => {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    for (let material of ensureArray(object.material)) {
                        material.dispose();
                    }
                }
                if (object.dispose) object.dispose();

                if (object.material) {
                    for (let material of ensureArray(object.material)) {
                        material.needsUpdate = true;
                    }
                }
            });
        }

        disposeObjectContext(scene);

        postProcessing = new PostProcessing({
            manager: this,
            renderer: rendererMain,
            scene,
            camera
        });

        webVrWrapper = new WebVrWrapper(rendererMain);
        webGLRaycaster.dispose();

        this.setSize(vpWidth, vpHeight);

        this.compile(scene, camera);

        camera.update = true;
    }

    setActiveScene(inScene) {
        if (activeScene) scene.remove(activeScene);

        scene.add(inScene);
        activeScene = inScene;
        activeScene.fog = fog.activeFog;

        Network.setActive(activeScene);
    }

    /**
     *
     * @param {THREE.WebGLRendererParameters} glParams WebGL render context parameters.
     * @param {GLRaycasterParams} rcParams Raycaster parameters.
     */
    init(glParams = {}, rcParams = {}, camData, _passByRaycasterLoader) {
        if (isInitialized) throw new Error("Already initiallized.");

        groundPlane.init();
        Network.mainWorld = activeScene;
        Network.registerWorld(activeScene);
        Network.setActive(activeScene);

        scene.add(activeScene);
        activeScene = activeScene;
        fog = new Q3Fog();

        initCamera(camData);

        initGlParams = glParams;
        initRcParams = rcParams;
        passByRaycasterLoader = _passByRaycasterLoader;

        if (Utils.queryParams.has("webgl2")) {
            const canvas = glParams.canvas || document.createElement("canvas");
            const context = glParams.context || Q3.browserCapabilities.isWebGl2 ? canvas.getContext("webgl2") : (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));

            glParams.canvas = canvas;
            glParams.context = context;
        }

        rendererMain = new THREE.WebGLRenderer(glParams);
        webGLRaycaster = new GLRaycaster(rcParams);
        postProcessing = new PostProcessing({ manager: this, renderer: rendererMain, scene, camera });
        webVrWrapper = new WebVrWrapper(rendererMain);
        background = new Q3Background(canvasHolder);

        rendererMain.context.canvas.addEventListener("webglcontextlost", function (event) {
            console.error(event);
        }, false);

        // rendererMain.setScissorTest(true);

        const gl = rendererMain.getContext();
        supportedTextures = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);

        canvasHolder.appendChild(rendererMain.domElement);
        domElement.appendChild(overlay2d);
        domElement.appendChild(overlaySVG);

        if (!Q3.editMode && !Q3.buildMode && Utils.queryParams.has("display_fps"))
            overlay2d.appendChild(stats.domElement);

        overlay2d.setAttribute("class", `overlay2d locale-${Q3.language ? Q3.language : 'en'}`);
        overlay2d.setAttribute("id", "overlay2d");
        overlaySVG.setAttribute("class", "overlay2d");
        overlaySVG.style.pointerEvents = "none";

        Object.defineProperty(domElement, "width", {
            get: () => { return vpWidth; },
            set: () => { throw new Error("Unsupported operation 'width'."); }
        });

        Object.defineProperty(domElement, "height", {
            get: () => { return vpHeight; },
            set: () => { throw new Error("Unsupported operation 'height'."); }
        });

        isInitialized = true;

        const setUpOutlineQuizAnswers = () => {
            const deferredPasses = postProcessing.passOutlineQuizAnswers;

            const initialized = ["correct", "wrong"].every(key => deferredPasses[key].get());
            if (!initialized) {
                requestAnimationFrame(setUpOutlineQuizAnswers);
                return;
            }

            const objects = new WeakMap();
            const highlight = {};

            this._quizHighlight = { objects, highlight };

            for (const key of ["correct", "wrong"]) {
                const deferredPass = deferredPasses[key];
                const pass = deferredPass.get();
                const selectedObjects = pass.selectedObjects;

                const setIndex = (object, index) => {
                    let objHl = objects.get(object);
                    if (!objHl) {
                        objHl = { highlight: key };
                        objects.set(object, objHl);
                    } else if (objHl.highlight !== key) {
                        throw new Error('attempt to set a highlighting mode when a different one is active for the same object');
                    }

                    objHl.index = index;
                };

                const getIndex = (object) => {
                    const objHl = objects.get(object);
                    return objHl && objHl.highlight === key ? objHl.index : null;
                };

                const deleteIndex = (object) => {
                    objects.delete(object);
                };

                const selObjs = new ArraySet(selectedObjects, setIndex, getIndex, deleteIndex);

                highlight[key] = selObjs;
            }
        };
        setUpOutlineQuizAnswers();

        this._initializeResolve();

        // Workaround for https://github.com/mrdoob/three.js/issues/9108
        // in case of using logaritmic depth buffer the shadows from directinal light are not working
        // this workeround disables log depth buffer only for shadow map rendering
        // const originalRenderFunf = rendererMain.shadowMap.render;
        // rendererMain.shadowMap.render = function () {
        //     const tmp = rendererMain.capabilities.logarithmicDepthBuffer;
        //     rendererMain.capabilities.logarithmicDepthBuffer = false
        //     originalRenderFunf.apply(rendererMain.shadowMap, arguments);
        //     rendererMain.capabilities.logarithmicDepthBuffer = tmp;
        // }

        listen("viewportchanged", () => background.updateSize());

        checkSeamless(rendererMain);

        return this;
    }

    getSupportedExtensions() { return rendererMain.context.getSupportedExtensions(); }

    setSize(w, h, updateStyle) {
        const capabilities = Q3.browserCapabilities;
        const pixelRatio = capabilities.pixelScale * (
            capabilities.useNativePixelRatio
                ? window.devicePixelRatio || 1
                : 1
        );

        canvasHolder.style.width = w + "px";
        canvasHolder.style.height = h + "px";
        rendererMain.setSize(w, h, updateStyle);
        rendererMain.setPixelRatio(pixelRatio);
        webGLRaycaster.resize(w, h);
        postProcessing.composer.setSize(w * pixelRatio, h * pixelRatio);

        const rect = overlay2d.getBoundingClientRect();
        boundingRect.min.set(rect.left, rect.top);
        boundingRect.max.set(rect.right, rect.bottom);

        vpWidth = w;
        vpHeight = h;

        if (background) background.reloadBackground();

        return this;
    }

    getSize() {
        const size = rendererMain.getSize();
        return g_size.set(size.width, size.height);
    }

    updatePostProcessing(lastRenderer, data, editMode) {
        postProcessing.updatePostProcessing({
            lastRenderer, ppData: data, editMode,
            enableHighlight, enableOutline
        });

        return this;
    }

    set enableHighlight(val) { enableHighlight = val; }
    get enableHighlight() { return enableHighlight };

    set enableOutline(val) { enableOutline = val; }
    get enableOutline() { return enableOutline; }

    get domElement() { return domElement; }

    set toneMapping(val) { rendererMain.toneMapping = val; }
    get toneMapping() { return rendererMain.toneMapping; }

    set toneMappingExposure(val) { rendererMain.toneMappingExposure = val; }
    get toneMappingExposure() { return rendererMain.toneMappingExposure };

    set gammaInput(val) { rendererMain.gammaInput = val; }
    get gammaInput() { return rendererMain.gammaInput; }

    set gammaOutput(val) { rendererMain.gammaOutput = val; }
    get gammaOutput() { return rendererMain.gammaOutput; }

    get info() { return rendererMain.info; }

    get shadowMap() { return rendererMain.shadowMap; }

    get pixelRatio() { return rendererMain.getPixelRatio(); }

    set localClippingEnabled(val) { rendererMain.localClippingEnabled = val; }
    get localClippingEnabled() { return rendererMain.localClippingEnabled; }

    getViewPort() { return viewPort.freeze(this.getSize()); }
    setViewport(left, top, width, height) {
        viewPort.set(left, top, width, height);

        rendererMain.setViewport(left, top, width, height);
        webGLRaycaster.setViewport(left, top, width, height);

        /**
         * @type {WebGLRenderingContext}
         */
        const gl = rendererMain.getContext();

        gl.enable(gl.SCISSOR_TEST);

        const { width: cw, height: ch } = this.getSize();
        const boxes = [
            [0, 0, left, ch],
            [left + width, 0, cw - left - width, ch],
            [left, 0, width, ch - height - top],
            [left, ch - top, width, top]
        ].map(arr => arr.map(x => x * devicePixelRatio));

        const debugClear = Utils.queryParams.has("debug_viewport_clear");

        boxes.forEach(([x, y, w, h]) => {
            x = Math.max(0, x);
            y = Math.max(0, y);

            w = Math.max(0, w);
            h = Math.max(0, h);

            gl.scissor(x, y, w, h);

            if (debugClear) { gl.clearColor(Math.random(), Math.random(), Math.random(), 1); }

            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
        });

        if (debugClear) { gl.clearColor(0, 0, 0, 0); }

        gl.disable(gl.SCISSOR_TEST);
    }

    getScissor() { return scissorSize.freeze(this.getSize()); }
    setScissor(x, y, w, h) {
        scissorSize.set(x, y, w, h);

        rendererMain.setScissor(x, y, w, h);
        webGLRaycaster.setScissor(x, y, w, h);
    }

    get overlay2d() { return overlay2d; }
    get overlay2dBounds() { return boundingRect; }
    get overlaySVG() { return overlaySVG; }
    get webVrWrapper() { return webVrWrapper; }
    get raycaster() { return webGLRaycaster; }

    enqueRaycaster(force = false) { webGLRaycaster.enqueFrame(rendererMain, camera, force); }
    enqueRaycasterAndAwait() {
        const prevRevision = webGLRaycaster.revision;

        return new Promise(resolve => {
            function _await() {
                const revision = webGLRaycaster.revision;

                if (revision === prevRevision) requestAnimationFrame(_await);
                else resolve();
            }

            _await();

            this.enqueRaycaster(...arguments);
        });
    }

    compile(scene, camera) { rendererMain.compile(scene, camera); }

    getExtension(ext) { return rendererMain.context.getExtension(ext); }
    getParameter(param) { return rendererMain.context.getParameter(param); }

    get mainRenderer() { return rendererMain; }
    get isRaycasterReady() { return webGLRaycaster.isReady; }

    update() { webGLRaycaster.updateBuffers(rendererMain); }

    compose(delta) {

        const passOutlineTouch = postProcessing.passOutlineTouch.get();

        if (passOutlineTouch) while (passOutlineTouch.selectedObjects.pop()) { }

        const objectsToSnap = [];

        if (!sceneBoundingBoxResolved) scene.updateMatrixWorld();

        sceneBoundingBox.makeEmpty();
        scene.traverseVisible(object => {
            if (
                (object.isTerrainCar ||
                    object.isRacetrack ||
                    object.isHotspotMesh ||
                    object.isUpdatable) && object.visible
            ) object.update();

            if (object.material) {
                for (let material of ensureArray(object.material)) {
                    if (material.isMeshNestedMaterial) {
                        material.updateLayers(object, webGLRaycaster);
                    }
                }
            }

            if (passOutlineTouch && object.isMesh) {
                const dataObject = SlideManager.currentSlide.animation[object.uuid];
                const extendedHighlighting = dataObject && "extendedSelectionEnabled" in dataObject
                    ? dataObject.extendedSelectionEnabled
                    : false;

                if (extendedHighlighting)
                    passOutlineTouch.selectedObjects.push(object);
            }

            const objectData = Q3.data.assets.objects[object.uuid];

            if (objectData) {
                if (object.isMesh && objectData && Network.insideCurrentWorld(object)) {
                    const geometry = object.geometry;
                    if (geometry) {
                        if (!geometry.boundingBox) {
                            geometry.computeBoundingBox();
                        }

                        if (!geometry.boundingBox.isEmpty()) {
                            sceneBoundingBox.expandByObject(object, false);
                        }
                    }
                }

                const objectSlideData = SlideManager.currentSlide.animation[object.uuid];

                if (objectSlideData.snapToGround)
                    objectsToSnap.push(object);
            }
        });

        if (!sceneBoundingBoxResolved) {
            sceneBoundingBoxResolved = true;
            sceneBoundingBoxResolve(sceneBoundingBox);
        }

        objectsToSnap.forEach(object => {
            const sceneMinY = sceneBoundingBox.min.y;
            const objMinY = tmpBoundingBox.setFromObject(object).min.y;

            if (!isFinite(sceneMinY) || !isFinite(objMinY)) return;

            const offset = sceneMinY - objMinY;

            if (Math.abs(offset) < 1e-5) return;

            const worldInverse = tmpMatrix.getInverse(object.parent.matrixWorld);
            const offsetVec = tmpVec3.set(0, offset, 0).applyMatrix4(worldInverse);

            object.position.add(offsetVec);

            Object.assign(SlideManager.currentSlide.animation[object.uuid].position, {
                x: object.position.x,
                y: object.position.y,
                z: object.position.z
            });
        });

        rendererMain.info.autoReset = false;

        const gatePass = postProcessing.passRenderGates.get();
        if (gatePass) { gatePass.setGates(Network.gates); }

        groundPlane.needsUpdate = true;
        groundPlane.render(rendererMain, activeScene, sceneBoundingBox);

        const defaults = SystemDefaults.defaultShadowSettings();
        const slide = SlideManager.currentSlide;
        const isObjRotation = slide.controls.interactionType === "objectRotation"
        const shadowSettings = slide.groundShadow || {};
        const enabled = isObjRotation ? false : "enabled" in shadowSettings ? shadowSettings.enabled : defaults.enabled;

        groundPlane.setMaterialVisibility(enabled);

        postProcessing.composer.render(delta);

        groundPlane.visible = false;

        function selectVisibleObjects() {

            let triangles = 0;
            if (SlideManager.currentSlide.layers.Renderer)
                [Network.world, { scene: Network.parent }]
                    .concat(Network.gates.filter(x => x.visible))
                    .forEach(world => {
                        world.scene && world.scene.traverseVisible(object => {
                            if (Q3.data.assets.objects[object.uuid] && object.geometry) {

                                const index = object.geometry.getIndex();
                                const vertices = !index && object.geometry.getAttribute("position");

                                triangles = triangles + (index ? index.count : vertices ? vertices.count : 0) / 3;

                            }
                        });
                    });

            return triangles;
        }

        lastFaceCount = selectVisibleObjects();

        rendererMain.info.autoReset = true;
    }

    get isContextLost() { return rendererMain.getContext().isContextLost(); }
    getSceneBoundingBox(dst) { return dst.copy(sceneBoundingBox); }

    waitSceneBoundingBox(dst) {
        return new Promise((resolve, reject) => {
            sceneBoundingBoxPromise.then(sbbox => resolve(dst.copy(sbbox)));
        });
    }

    setPreselectObjects(objects = [], passName = "passOutlinePreselect") {
        if (!postProcessing[passName]) return;

        const pass = postProcessing[passName].get();

        if (!pass) return;

        while (pass.selectedObjects.pop()) { }

        objects = objects instanceof Array ? objects : [objects];

        objects.forEach(object => {
            if (object) pass.selectedObjects.push(object);
        });
    }

    setQuizHighlightObject(object, highlight = null) {
        const quizHighlight = this._quizHighlight;

        const objHighlight = quizHighlight.objects.get(object);
        const oldHighlight = objHighlight ? objHighlight.highlight : null;

        if (oldHighlight === highlight) {
            return;
        }

        if (highlight !== null && highlight !== 'correct' && highlight !== 'wrong') {
            throw new Error('invalid highlight mode: '+highlight);
        }

        if (oldHighlight) {
            quizHighlight.highlight[oldHighlight].delete(object);
        }

        if (highlight) {
            quizHighlight.highlight[highlight].add(object);
        }
    }

    setOutlineObjects(objects = []) {
        const pass = postProcessing.passOutline.get();

        if (!pass) return;

        while (pass.selectedObjects.pop()) { }

        objects = objects instanceof Array ? objects : [objects];

        objects.forEach(object => {
            if (object) pass.selectedObjects.push(object);
        });
    }

    setHighlightObjects(objects = []) {
        const pass = postProcessing.passHighlight.get();

        if (!pass) return false;

        while (pass.selectedObjects.pop()) { }

        const slideData = SlideManager.currentSlide;
        const animation = slideData && slideData.animation;

        objects = objects instanceof Array ? objects : [objects];
        Object.keys(Q3.data.assets.objects).forEach((uuid) => {
            const objectData = Q3.data.assets.objects[uuid];
            const animData = animation[uuid];

            const highlight = animData && 'highlight' in animData ? animData.highlight : objectData.highlight;

            if (highlight === "on") {
                objects.push(Q3.assets.objects[uuid])
            }
        });
        objects.forEach(object => {
            if (object) pass.selectedObjects.push(object);
        });

        return true;
    }

    deferredSetHighlightObjects(...args) {
        let attemptSetHighlightObjects = () => {
            if (!this.setHighlightObjects(...args)) {
                requestAnimationFrame(attemptSetHighlightObjects);
            }
        };

        attemptSetHighlightObjects();
    }

    toDataURL() { return rendererMain.domElement.toDataURL(); }

    clearOverlay(overlay) { overlay.clear(rendererMain); }

    renderOverlay(overlay, params) { overlay.render(rendererMain, scene, camera, params); }

    get faces() { return lastFaceCount; }

    get root() { return scene; }

    get maxTextureUnits() { return supportedTextures; }

    getRenderViewportSize() {
        const { width: vpWidth, height: vpHeight } = this.getSize();

        const rData = getRenderData(Q3.mobileView);
        const isResized = !Q3.editMode && (rData.top > 1e-3 || rData.left > 1e-3 || rData.width < 1 - 1e-3 || rData.height < 1 - 1e-3);
        const letterbox = !isResized
            ? { x: 0, y: 0, w: vpWidth, h: vpHeight }
            : getLetterbox();

        const fullWidth = Math.round(letterbox.w);
        const fullHeight = Math.round(letterbox.h);
        const width = Math.round(rData.width * fullWidth);
        const height = Math.round(rData.height * fullHeight);
        const offsetLeft = Math.round(letterbox.x + rData.left * fullWidth);
        const offsetTop = Math.round(letterbox.y + rData.top * fullHeight);

        return {
            x: offsetLeft,
            y: offsetTop,
            width: width,
            height: height
        };
    }

    toScreenCoords(x, y, out = new THREE.Vector2()) {
        const vp = this.getRenderViewportSize();
        const px = Math.round((vp.width * x + vp.width) * 0.5) + vp.x;
        const py = Math.round((vp.height - vp.height * y) * 0.5) + vp.y;

        return out.set(px, py);
    }

    toScreenSpaceCoords(x, y, out = new THREE.Vector2()) {
        if (y === undefined) {
            y = x.y;
            x = x.x;
        }

        const vpWidth = viewPort.right - viewPort.left;
        const vpHeight = viewPort.bottom - viewPort.top;

        out.x = (x - viewPort.left) / vpWidth * 2 - 1;
        out.y = 1 - (y - viewPort.top) / vpHeight * 2;

        return out;
    }

    updateAnimations(delta) {
        Q3.mixer.update(delta * 2);
        updateSplines(clock.getElapsedTime());
        Q3.labels.live.updateTransition();

        if (Q3.mixers) {
            for (let i in Q3.mixers) {
                if (Q3.mixers[i].clock) handleTransitionAnimation(i);

                if (!!Q3.mixers[i]._actions[0] && Q3.mixers[i]._actions[0].enabled) {
                    camera.update = true;
                    this.shadowMap.needsUpdate = true;
                }
            }
        }
    }

    get isPaused() { return isPaused; }
    pause() { isPaused = true; }
    resume() {
        isPaused = false;
        this.doRender(0);
    }

    doRender(currentFrameTime) {
        if (isPaused) {
            requestAgain();
            return;
        }

        this._doRender(currentFrameTime);

        requestAnimationFrame(doRender);
    }

    _doRender(currentFrameTime) {
        background.update();

        containsAnimatedObjects = false;

        const raycasterLoadTime = Q3.editMode ? 20 : 5;
        const faceSorterLoadTime = Q3.editMode ? 100 : 10;
        passByRaycasterLoader.resumeLoading(Date.now() + raycasterLoadTime);
        FaceSorter.resumeLoading(faceSorterLoadTime);

        checkLODAssets();

        if (this.isRaycasterReady) {
            stats.update();

            if (Q3.editMode) { fire("updatefpsdisplay"); }

            this.update();
            return;
        }

        AssessmentManager.update();

        if (!AssessmentManager.inAssessmentMode)
            updateInteractableGeometries();

        updateMMMObjects(Input.mouse, camera, Q3.controls);
        updateTerrainCars(Input.mouse, camera, Q3.controls);

        const deltaTime = currentFrameTime - currentTime;

        if (!Utils.queryParams.has("disable_fps_cap")) {
            //update time dependent animations here at 30 fps
            if (deltaTime < interval) return;
        }

        if (this.isContextLost) {
            this.tryRestoreContext();
            return;
        }

        const frameTime = deltaTime / 1000;

        currentTime = currentFrameTime;
        rendererRevision++;

        Network.updateTransfer(sceneBoundingBox, camera, Q3.controls);

        if (Q3.controls) Q3.controls.update();

        let slideControls = SlideManager.currentSlide.controls;

        if (slideControls && slideControls.interactionType === 'objectRotation') {
            Q3.physicalCameraControls.update();
        }

        if (Q3.mixer._nActiveActions > 0) {
            camera.update = true;
            this.shadowMap.needsUpdate = true;
        }

        let hasUpdated = false;

        if (Q3.data.setups.postprocess.nonPermanent) {
            if (camera.update) {
                if (postProcessing.lastRender || camera.update) postProcessing.set(camera.update || false);

                render(frameTime);
                hasUpdated = true;

                postProcessing.lastRender = true;
            } else if (!Input.mouse.isDown && postProcessing.lastRender) {
                if (!postProcessing.lastRender || camera.update) postProcessing.set(true);

                render(frameTime);
                hasUpdated = true;

                postProcessing.lastRender = false;
            }
        } else {
            postProcessing.set(false);

            render(frameTime);
            hasUpdated = true;
        }

        if (!Utils.queryParams.has("force_render")) { camera.update = false; }

        stats.update();

        if (Q3.editMode) { fire("updatefpsdisplay"); }

        if (raycasterRevision !== webGLRaycaster.revision || hasUpdated) {
            Q3.labels.live.update();
        } else if (Q3.labels.live.labelsLerping || Q3.labels.live.labelsTransitioning) {
            Q3.labels.live.updateTransition();
            Q3.labels.live.update();
        }

        raycasterRevision = webGLRaycaster.revision;

        if (containsAnimatedObjects) {
            camera.update = true;
        }
    }
}

export default RenderManager;
export { RenderManager };

function render(delta = 0.3) {
    if (Q3.layers) Q3.layers.render();

    if (Q3.editMode && Q3.transformControls.visible) Q3.transformControls.update();

    if (RenderManager.info.reset) RenderManager.info.reset();

    RenderManager.updateAnimations(delta);
    if (RenderManager.webVrWrapper.active) {
        RenderManager.webVrWrapper.render(activeScene, camera);
    } else { RenderManager.compose(delta); }


    if ((Q3.labels.live.isDisplayingRegions || Q3.labels.live.blobCreationEnabled) && Q3.selected)
        Q3.labels.live.renderRegions(camera, Q3.selected);

    if (!Input.mouse.isDown && wasDown) {
        wasDown = false;
        Q3.labels.live.clearLast();
    } else if (!wasDown) {
        wasDown = true;
        Q3.labels.live.clearLast();
    }

    RenderManager.enqueRaycaster();

    fire("post_render");
}

function handleTransitionAnimation(i) {
    const mixer = Q3.mixers[i];
    let action = mixer._actions[0];
    const animStart = mixer.animStart ? mixer.animStart : 0;
    const animEnd = mixer.animEnd ? mixer.animEnd : parseFloat(action._clip.duration.toFixed(2));

    if (action.loop === THREE.LoopOnce || action.loop === THREE.LoopRepeat) {
        if (mixer.repetition !== action.repetitions + 1) {
            if (action.time >= animStart && action.time <= animEnd) {
                mixer.update(mixer.clock.getDelta());
            } else if (action.time < animStart) {
                action.time = animStart;
            } else if (action.loop === THREE.LoopRepeat) {
                action.time = animStart;
                mixer.repetition++;
            }
        } else { mixer.clock.stop(); }
    } else if (action.loop === THREE.LoopPingPong) {
        //update forward and update backward alternately
        if (mixer.repetition !== action.repetitions + 1) {
            if (mixer.repetition % 2 === 0) playForwardInRange(mixer, action, animStart, animEnd);
            else playBackwardInRange(mixer, action, animStart, animEnd);
        }
    }
}

function playForwardInRange(mixer, action, animStart, animEnd) {
    if (action.time >= animStart && action.time < animEnd) {
        if (!mixer.clock.running) mixer.clock.start();

        mixer.update(mixer.clock.getDelta());
    } else if (action.time < animStart) {
        action.time = animStart;
    } else {
        mixer.clock.stop();
        mixer.repetition++;
    }

}

function playBackwardInRange(mixer, action, animStart, animEnd) {

    if (action.time > animStart && action.time <= animEnd) {
        if (!mixer.clock.running) mixer.clock.start();
        mixer.update(-mixer.clock.getDelta());
    }
    else if (action.time <= animStart) {
        mixer.clock.stop();
        mixer.repetition++;
    } else { action.time = animEnd; }
}

function initCamera(camData) {
    const style = getStyle();
    const aspect = style.width / style.height;

    camera.fov = SlideManager.currentSlide.camera.fov || camData.fov;
    camera.aspect = aspect;
    camera.near = PLANE_NEAR;
    camera.far = PLANE_FAR;

    camera.updateProjectionMatrix();

    camera.activeCameraType = "perspective";
    camera.name = "Camera";

    camera.position.z = -5;
    camera.update = true;
}

function createEmptyBounds() {
    return Object.seal({
        x: NaN,
        y: NaN,
        left: NaN,
        top: NaN,
        bottom: NaN,
        right: NaN,
        width: NaN,
        height: NaN
    });
}

function checkLODAssets() {
    if (!Q3.editMode)
        AssetDeserializer.checkLODs();
}

function requestAgain() {
    checkLODAssets();
    requestAnimationFrame(requestAgain);
}

function doRender(time) { RenderManager.doRender(time); }

domElement.addEventListener("drop", function (event) {
    event.preventDefault();

    const action = event.dataTransfer.getData("action");

    if (!action) return;

    const data = event.dataTransfer.getData("data");

    async function addObject(data) {
        const { object, noSelect } = await ObjectManager.addObject(data);
        const wCamPos = camera.getWorldPosition(new THREE.Vector3());
        const wObjPos = object.getWorldPosition(new THREE.Vector3());
        const boundingBox = new THREE.Box3().setFromObject(object);
        const bsphere = boundingBox.getBoundingSphere(new THREE.Sphere());
        const offsetVec = new THREE.Vector3().subVectors(wObjPos, bsphere.center);
        const mouseEnd = getRelativeCoordinates(event);
        const mouseScreenspace = RenderManager.toScreenSpaceCoords(mouseEnd);
        const mouseWorld = new THREE.Vector3().copy(mouseScreenspace).setZ(0.5).unproject(camera);
        const mouseDir = new THREE.Vector3().subVectors(mouseWorld, wCamPos);
        const objOffset = bsphere.radius * 3 / Math.tan(Q3.RenderManager.activeCamera.fov * THREE.Math.DEG2RAD);
        const inverse = new THREE.Matrix4().getInverse(object.parent.matrixWorld);

        object.position
            .copy(mouseDir)
            .setLength(objOffset)
            .add(wCamPos)
            .add(offsetVec)
            .applyMatrix4(inverse);

        SlideManager.slides.forEach(slide => {
            ["x", "y", "z"].forEach(ax => {
                slide.animation[object.uuid].position[ax] = object.position[ax];
            });
        });

        if (!noSelect) Q3.selected = object;
        fire("selectchange");

        RenderManager.activeCamera.update = true;

        // something borks the envmap initially
        setTimeout(() => RenderManager.activeCamera.update = true, 100);
    }

    switch (action) {
        case "add-object": addObject(data); break;
    }
});
